import { DishAllergen } from "ts-packages/api-types/src";

import { useCallback, useState } from "react";

import { ButtonBase, Stack, Typography, useTheme } from "@mui/material";

import { DynamicDrawer, Search } from "igfd-ui/src";
import { formatName } from "igfd-ui/src/utils/stringUtils";

import { useRestaurantContext } from "../../hooks/useRestaurantContext";
import { trackStaffEvent } from "../../monitoring/mixpanel";
import { useSearchTracking } from "../../monitoring/useSearchTracking";
import { AllergenList } from "../allergen-list/allergen-list";
import { IngredientList } from "../ingredient-list/ingredient-list";
import { PreferenceDetected } from "../preference/preference-detected";
import { SubProductList } from "./sub-product-list";

interface ProductDrawerProps {
  open: boolean;
  productId?: string;
  onClose: () => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}

export const ProductDrawer = ({
  open,
  productId,
  onClose,
  containerRef,
}: ProductDrawerProps) => {
  const {
    palette: { oscar },
  } = useTheme();
  const [filter, setFilter] = useState("");
  const [selectedAllergenIds, setSelectedAllergenIds] = useState<string[]>([]);
  const { linkedItems } = useRestaurantContext();

  const handleSelectAllergen = useCallback(
    (allergen: DishAllergen) => {
      if (!selectedAllergenIds.includes(allergen.igfdTaxAllergenId)) {
        trackStaffEvent("Allergen Checked", {
          allergen_id: allergen.igfdTaxAllergenId,
          allergen_name: allergen.allergenName,
          where: "product",
        });
      }
      setSelectedAllergenIds((prevIds) => {
        const index = prevIds.findIndex(
          (id) => id === allergen.igfdTaxAllergenId
        );
        if (index === -1) {
          return [...prevIds, allergen.igfdTaxAllergenId];
        }
        return prevIds.filter((id) => id !== allergen.igfdTaxAllergenId);
      });
    },
    [selectedAllergenIds]
  );
  useSearchTracking("ingredients", filter);

  const product = !!productId ? linkedItems?.products[productId] : undefined;
  if (!product) {
    return null;
  }

  return (
    <DynamicDrawer
      ref={containerRef}
      drawerType="medium"
      open={open}
      onClose={onClose}
      Header={
        <Stack alignItems="center" mb={6}>
          <Typography variant="headingRLight" textAlign="center">
            {formatName(product.name)}
          </Typography>
        </Stack>
      }
      Footer={
        <ButtonBase
          sx={{
            backgroundColor: oscar.light.surface.black,
            borderRadius: 32,
            height: 48,
            width: 168,
          }}
          onClick={onClose}
        >
          <Typography
            color={oscar.light.content.clear}
            variant="buttonL"
            textAlign="center"
          >
            Close
          </Typography>
        </ButtonBase>
      }
    >
      <Stack gap={4} paddingBottom={4}>
        <PreferenceDetected preference={product.mostRestrictivePreference} />

        <AllergenList
          allergens={product.allAllergens}
          mayContainAllergens={product.allMayContainAllergens}
          selectedAllergenIds={selectedAllergenIds}
          onSelectAllergen={handleSelectAllergen}
        />

        <Stack gap={1} width="100%">
          <Typography variant="labelRMedium">
            {`${
              (product?.directlyLinkedProducts?.length || 0) +
              (product?.directlyLinkedIngredients?.length || 0)
            } items in this product`}
          </Typography>
          <Search
            filter={filter}
            setFilter={setFilter}
            placeholder="Search ingredients"
          />
        </Stack>

        {product.directlyLinkedProducts?.length ? (
          <SubProductList
            subProducts={product.directlyLinkedProducts}
            filter={filter}
            selectedAllergenIds={selectedAllergenIds}
          />
        ) : null}

        <IngredientList
          filter={filter}
          ingredients={product.allIngredients}
          directlyLinkedIngredients={product.directlyLinkedIngredients}
          selectedAllergenIds={selectedAllergenIds}
        />
      </Stack>
    </DynamicDrawer>
  );
};
